import { Injectable } from '@angular/core';
import { AdminService } from '@services/admin.service';

/**
 * Servicio central que encapsula toda la interacción con la API global de
 * Quantum Metric (`window.QuantumMetricAPI`).
 *
 * Responsabilidades:
 * - Concentrar la lectura de la feature flag `allowSessionRecording`
 *   (`isEnabled()`), fuente única de la decisión de habilitación.
 * - Iniciar/detener la sesión de grabación de QM garantizando idempotencia
 *   (sin sesiones duplicadas).
 * - Publicar/limpiar el atributo persistente `QMReplayLinkKiosk` en
 *   `window.newrelic`.
 * - Exponer `getReplayLink()` de solo lectura para los wrappers de New Relic.
 * - Registrar el listener `'start'` una sola vez para cubrir la carga
 *   asíncrona del script de QM.
 *
 * Principio rector: **impacto cero en el flujo de negocio**. Cuando la flag
 * está apagada/ausente o QM no está disponible, el comportamiento del kiosko es
 * idéntico al actual. Toda operación de QM está envuelta en `try/catch` y
 * guardas defensivas, de modo que ningún fallo de QM se propaga al flujo de
 * negocio del pasajero.
 */
@Injectable({ providedIn: 'root' })
export class QuantumMetricService {
  /** Nombre del atributo persistente publicado en New Relic. */
  private static readonly REPLAY_ATTR = 'QMReplayLinkKiosk';

  /** Idempotencia + guarda de `getReplayLink()`: la sesión activa es del kiosko. */
  private sessionActive = false;

  /** El listener `'start'` se registra una sola vez. */
  private startListenerRegistered = false;

  /** START solicitado antes de que el script de QM estuviera disponible. */
  private pendingStart = false;

  /**
   * Último replay link publicado en New Relic. Sirve de guarda anti-duplicado:
   * si al iniciar una nueva sesión QM devuelve el MISMO link (señal de que no
   * rotó la grabación), no se republica —se publica `''`— para no atribuir la
   * grabación del pasajero anterior a las métricas del siguiente.
   */
  private lastPublishedLink = '';

  constructor(private readonly _adminService: AdminService) {}

  /** Acceso tipado y defensivo a la API global de QM. */
  private get api(): QuantumMetricAPI | undefined {
    return typeof window !== 'undefined' ? window.QuantumMetricAPI : undefined;
  }

  /**
   * Inicia la grabación de sesión de QM si y solo si la flag
   * `allowSessionRecording` está habilitada, `window.QuantumMetricAPI` está
   * disponible y no hay una sesión activa (idempotencia).
   *
   * Si el método de arranque de QM aún no está expuesto (carga asíncrona), se
   * marca `pendingStart` y la sesión se activará al recibir el evento `'start'`.
   * Cualquier excepción de QM se captura y registra sin propagarse.
   */
  public startSession(): void {
    try {
      if (!this.isEnabled()) return; // flag OFF/ausente -> no-op
      if (this.sessionActive) return; // idempotencia
      const api = this.api;
      if (!api) return; // QM no disponible -> sessionActive queda false

      this.registerStartListenerOnce(api);

      // Arranque con `optInUser()`: método oficial de QM para "limpiar la cookie
      // y reiniciar la sesión". Garantiza una sesión/replay NUEVO por reserva,
      // de modo que cada pasajero obtiene un replay link distinto (la cookie
      // anterior se descarta). `newUser()`/`newSession()` quedan como fallback
      // para entornos donde `optInUser` no esté expuesto.
      if (typeof api.optInUser === 'function') {
        api.optInUser();
        this.sessionActive = true;
        this.publishReplayLink();
      } else if (typeof api.newUser === 'function') {
        api.newUser();
        this.sessionActive = true;
        this.publishReplayLink();
      } else if (typeof api.newSession === 'function') {
        api.newSession();
        this.sessionActive = true;
        this.publishReplayLink();
      } else {
        this.pendingStart = true; // esperar al evento 'start'
      }
    } catch (e) {
      console.warn('[QuantumMetric] startSession failed:', e);
    }
  }

  /**
   * Finaliza la grabación de sesión activa y limpia el atributo
   * `QMReplayLinkKiosk` en New Relic. Es no-op si la flag está deshabilitada,
   * no hay sesión activa o `window.QuantumMetricAPI` no está disponible.
   * Cualquier excepción de QM se captura y registra sin propagarse.
   */
  public stopSession(): void {
    try {
      // Detener la grabación en QM solo tiene sentido si hay una sesión activa
      // y la API está disponible.
      if (this.sessionActive) {
        this.api?.stopSession?.();
      }
      this.sessionActive = false;

      // La limpieza del atributo de New Relic es INCONDICIONAL: `QMReplayLinkKiosk`
      // es persistente, así que si no se limpia en cada cierre, el link del
      // pasajero anterior seguiría viajando en las métricas del siguiente.
      // CRÍTICO: no debe estar supeditada a `isEnabled()`. Si la flag
      // `allowSessionRecording` se apaga DESPUÉS de grabar una sesión, aún así
      // hay que limpiar el atributo para no filtrar el replay link del pasajero
      // anterior a las sesiones siguientes. Tampoco depende de que `sessionActive`
      // fuera true ni de que QM entregue una sesión nueva.
      this.clearReplayLink();
    } catch (e) {
      console.warn('[QuantumMetric] stopSession failed:', e);
    }
  }

  /**
   * Devuelve el replay link (URL de repro) únicamente si la sesión fue iniciada
   * por el kiosko (`sessionActive === true`). En cualquier otro caso, o ante un
   * valor no-string / excepción de QM, devuelve la cadena vacía.
   *
   * @returns la URL de repro de la sesión activa, o `''`.
   */
  public getReplayLink(): string {
    try {
      if (!this.sessionActive) return ''; // guarda: solo sesión propia del kiosko
      const url = this.api?.getReplay?.();
      return typeof url === 'string' ? url : '';
    } catch (e) {
      console.warn('[QuantumMetric] getReplayLink failed:', e);
      return '';
    }
  }

  /** Única lectura de la feature flag, con optional chaining y default apagado. */
  private isEnabled(): boolean {
    return this._adminService?.admin?.allowSessionRecording === true;
  }

  /**
   * Publica el replay link de la sesión activa en New Relic (persistente).
   *
   * Guarda anti-duplicado: si QM devuelve el mismo link ya publicado en una
   * sesión anterior, significa que no rotó la grabación (p. ej. cuando el canal
   * local de QM no está disponible). En ese caso se publica `''` en lugar de
   * repetir el link, para que las métricas del pasajero actual no apunten a la
   * grabación del anterior.
   */
  private publishReplayLink(): void {
    const link = this.getReplayLink();
    const valueToPublish = link && link === this.lastPublishedLink ? '' : link;
    window.newrelic?.setCustomAttribute?.(
      QuantumMetricService.REPLAY_ATTR,
      valueToPublish,
      true
    );
    if (valueToPublish) {
      this.lastPublishedLink = valueToPublish;
    }
  }

  /**
   * Limpia el atributo `QMReplayLinkKiosk` en New Relic (cadena vacía).
   *
   * No se resetea `lastPublishedLink`: el recuerdo del último link publicado
   * debe sobrevivir al cierre para que, al iniciar la siguiente sesión, el
   * guard anti-duplicado de `publishReplayLink()` detecte si QM devuelve el
   * mismo link (sesión no rotada) y evite republicarlo.
   */
  private clearReplayLink(): void {
    window.newrelic?.setCustomAttribute?.(QuantumMetricService.REPLAY_ATTR, '', true);
  }

  /**
   * Registra —una sola vez— el listener `'start'` del ciclo de vida de QM para
   * cubrir la carga asíncrona del script. Al dispararse, si había un START
   * pendiente y la flag sigue habilitada, activa la sesión y publica el link.
   */
  private registerStartListenerOnce(api: QuantumMetricAPI): void {
    if (this.startListenerRegistered) return;
    api.addEventListener?.('start', () => {
      if (this.pendingStart && this.isEnabled()) {
        this.sessionActive = true;
        this.pendingStart = false;
        this.publishReplayLink();
      }
    });
    this.startListenerRegistered = true;
  }
}
